import {
  type TablePaginationConfig as AntdTablePaginationConfig,
  Table as AntTable,
  type TableProps as AntTableProps,
  type SelectProps,
} from "antd";

import { assertPaginationControlled } from "../shared/assertPaginationControlled";

import type { AvE2EProps } from "../../types";

type TablePaginationConfig = Omit<AntdTablePaginationConfig, "itemRender"> & {
  /**
   * @deprecated This prop is ignored and controlled manually in the central component
   */
  itemRender?: AntdTablePaginationConfig["itemRender"];
};

type Props<T> = AvE2EProps &
  Omit<AntTableProps<T>, "pagination"> & {
    /**
     * @deprecated use testIdPrefix instead
     */
    "data-testid"?: string;
    pagination?: TablePaginationConfig | false;
  };

function CustomTable<T>(props: Props<T>) {
  const { testIdPrefix, pagination, ...rest } = props;

  assertPaginationControlled(pagination, "<Table>", typeof rest.onChange === "function");

  const isLoading =
    rest.loading === true || (typeof rest.loading === "object" && rest.loading.spinning !== false);
  let locale = rest.locale;

  if (isLoading) {
    locale = { ...locale, emptyText: null };
  }

  const paginationTestId = `${testIdPrefix}-table-pagination`;
  let totalRows = 0;

  if (typeof pagination === "object" && typeof pagination.total === "number") {
    totalRows = pagination.total;
  } else if (Array.isArray(rest.dataSource)) {
    totalRows = rest.dataSource.length;
  }

  let finalPagination = !!pagination || typeof pagination === "object" ? { ...pagination } : false;

  if (typeof pagination === "undefined") {
    finalPagination = {};
  }

  if (finalPagination) {
    if (typeof finalPagination.showSizeChanger === "undefined") {
      const defaultPageSize = finalPagination.pageSize ?? finalPagination.defaultPageSize ?? 10;

      finalPagination.showSizeChanger = totalRows > defaultPageSize;
    }

    if (finalPagination.showSizeChanger) {
      const showSizeChangerProps =
        typeof finalPagination.showSizeChanger === "object" ? finalPagination.showSizeChanger : {};
      const pageSizeChangerProps: SelectProps & { "data-testid": string } = {
        ...showSizeChangerProps,
        "data-testid": `${paginationTestId}-page-size`,
      };

      finalPagination.showSizeChanger = pageSizeChangerProps;
    }

    //@ts-expect-error this prop works
    finalPagination["data-testid"] = `${testIdPrefix}-table-pagination`;

    finalPagination.itemRender = (
      page: number,
      type: "page" | "prev" | "next" | "jump-prev" | "jump-next",
      element: React.ReactNode
    ) => {
      switch (type) {
        case "page":
          return <span data-testid={`${paginationTestId}-page-${page}`}>{element}</span>;
        default:
          return <span data-testid={`${paginationTestId}-${type}`}>{element}</span>;
      }
    };
  }

  return (
    <AntTable<T>
      aria-busy={isLoading ? true : undefined}
      {...(rest as AntTableProps<T>)}
      data-testid={`${testIdPrefix}-table`}
      locale={locale}
      pagination={finalPagination}
    />
  );
}

export const Table = Object.assign(CustomTable, {
  Summary: AntTable.Summary,
});
